import { useState, useEffect } from "react";
import { api } from "../lib/api";

export default function Countdown({ target, className = "", size = "lg" }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = Math.max(0, new Date(target).getTime() - Date.now());
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  const pad = (n) => String(n).padStart(2, "0");
  const items = [
    { v: pad(t.d), l: "Jours" }, { v: pad(t.h), l: "Heures" },
    { v: pad(t.m), l: "Min" }, { v: pad(t.s), l: "Sec" },
  ];
  const big = size === "lg";

  return (
    <div className={`flex items-stretch gap-2 sm:gap-3 ${className}`} data-testid="countdown">
      {items.map((it, i) => (
        <div key={i} className="flex-1 border border-white/10 bg-void/60 px-2 py-3 text-center">
          <div className={`font-display font-700 tabular-nums text-cyan text-glow ${big ? "text-3xl sm:text-5xl" : "text-2xl"}`}>
            {it.v}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-ink-tertiary">{it.l}</div>
        </div>
      ))}
    </div>
  );
}

// ================================================================================
// Logique hebdomadaire PARTAGÉE (accueil, blog, challenge) :
// le compte à rebours repart chaque lundi à 00:00:01 pour 7 jours, et le compteur
// d'idées suit le nombre réel de projets déposés + 1 par heure au rythme de la semaine.
// ================================================================================
export const WEEK_MS = 7 * 24 * 3600 * 1000;

export function mondayStart(now = new Date()) {
  const d = new Date(now);
  const day = (d.getDay() + 6) % 7; // 0 = lundi
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 1, 0);
  if (d.getTime() > now.getTime()) d.setDate(d.getDate() - 7); // lundi 00:00:01 pile
  return d;
}

export function WeeklyCountdown({ target = null }) {
  // target : date personnalisée (mode admin « fixed ») ; sinon rebours hebdomadaire.
  const [weeklyTarget, setWeeklyTarget] = useState(() => new Date(mondayStart().getTime() + WEEK_MS));
  useEffect(() => {
    const id = setInterval(() => {
      if (Date.now() >= weeklyTarget.getTime()) setWeeklyTarget(new Date(mondayStart().getTime() + WEEK_MS));
    }, 1000);
    return () => clearInterval(id);
  }, [weeklyTarget]);
  return <Countdown target={target || weeklyTarget} />;
}

// Compteur d'idées/projets : base (réelle ou manuelle) + N par heure depuis lundi 00:00,
// paramétré dans l'admin. Retourne { count, pct } (pct = % de présélectionnés).
export function useLiveProjectCount() {
  const [cfg, setCfg] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    api.get("/public/weekly").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  if (!cfg || cfg.ideas_base == null) return null;
  const perHour = cfg.ideas_per_hour ?? 1;
  const hours = Math.max(0, (now - new Date(cfg.ideas_base_at).getTime()) / 3600000);
  return { count: Math.floor(cfg.ideas_base + perHour * hours), pct: cfg.presel_percent ?? 7 };
}
